import { Button, Form, Input, Alert, message } from "antd";
import { useState, useEffect } from "react";
import { formHasErrors } from "../../../../shared/utils/functions/handleFormChanges";
import { getImageUrl } from "../../../../shared/utils/functions/images";
import { useCreateNewInterviewKitMutation, useGetAllInterviewsTemplatesQuery } from "../../slice/interviewApiSlice";
import { useParams } from "react-router-dom";
import LazyLoader from "../../../../shared/components/LazyLoader/load";
import { apiStatuses, errorMessage } from '../../../../shared/utils/constantData';
import { useDispatch, useSelector } from 'react-redux'
import { setGenerateNewKit, selectAllKits, setAllKits, setSelectedKit, setSelectedTemplate, selectSelectedTemplate, setGenerateEvaluationKitModal, setIsEvaluationQuestionsModal, setSelectedKitTemplateData, selectAllTemplates, setTemplates } from "../../slice/interviewSlice"
import { CheckboxDropdownItem } from "../../../../shared/utils/interfaces/settingsInterface";
import ChevronDown from '../../../../shared/assets/images/ChevronDown.svg'
import DropdownWithButton from "../../../../shared/components/Dropdown/DropDownWithButton";
export const GenerateNewKitForm = ({ fromProfile = false, className }: { fromProfile?: boolean, className?: string }) => {
    const [form] = Form.useForm();
    const [isDisabled, setIsDisabled] = useState(true);
    const [onSubmitError, setOnSubmitError] = useState(false);
    const allKits = useSelector(selectAllKits);
    const templates = useSelector(selectAllTemplates)
    const dispatch = useDispatch();
    const { jobId } = useParams();
    const selectedTemplate = useSelector(selectSelectedTemplate);
    const [createNewInterviewKit, { data, isLoading, isSuccess, isError, error }] = useCreateNewInterviewKitMutation();
    const { data: templatesData, isSuccess: isTemplatesSuccess, isLoading: isTemplatesLoading, isError: isTemplatesError } = useGetAllInterviewsTemplatesQuery(undefined, { skip: !fromProfile });
    const ValidationError = (
        <img alt="Required" src={getImageUrl("ValidationError")} />
    );

    useEffect(() => {
        if (isTemplatesSuccess) {
            const formattedTemplates = Array.isArray(templatesData?.data) ? templatesData?.data?.map((template: { id: number; title: string }) => ({
                key: template?.id.toString(),
                label: template?.title,
            })) : [];
            dispatch(setTemplates(formattedTemplates))
        }
    }, [isTemplatesSuccess, templatesData, dispatch])

    useEffect(() => {
        if (isLoading) setOnSubmitError(false);
        else if (isSuccess) {
            setIsDisabled(true)
            dispatch(setGenerateNewKit(false))
            message.success(
                <>
                    <span className='text-green-800'>Interview kit successfully generated. </span>
                </>, 5)
            const newKit = {
                key: data?.data?.id,
                label: data?.data?.data?.title
            }
            const lastElement = allKits[allKits.length - 1];
            const otherKits = allKits.slice(0, -1);
            const childrenIndex = otherKits.findIndex((kit: CheckboxDropdownItem) => kit.children);
            let updatedKits = []
            if (fromProfile) {
                dispatch(setSelectedKitTemplateData(data?.data))
                dispatch(setGenerateEvaluationKitModal(false))
                dispatch(setSelectedTemplate({}))
                dispatch(setSelectedKit(newKit));
                form.resetFields();
                dispatch(setIsEvaluationQuestionsModal(true));
            } else {

                if (childrenIndex !== -1) {
                    const beforeChildren = otherKits.slice(0, childrenIndex);
                    const afterChildren = otherKits.slice(childrenIndex);
                    updatedKits = [...beforeChildren, newKit, ...afterChildren, lastElement];
                }
                else {
                    updatedKits = [...otherKits, newKit, lastElement]
                }
                dispatch(setAllKits(updatedKits));
                dispatch(setSelectedKit(newKit));
            }
        }
        else if (isError) message.error(error?.status === apiStatuses.badRequest ? errorMessage : error?.data?.message || errorMessage, 5)
    }, [isSuccess, isLoading, isError, error, dispatch, data, allKits, fromProfile, form])

    useEffect(() => {
        if (selectedTemplate?.key && fromProfile) {
            dispatch(setGenerateEvaluationKitModal(false))
            dispatch(setSelectedKit({}))
            form.resetFields();
            dispatch(setIsEvaluationQuestionsModal(true));
        }
    }, [selectedTemplate, dispatch, form, fromProfile])

    const onFinish = async (values: any) => {
        const keywords = values.keywords.split(",");
        const id = Number(jobId)
        const body = { ...values, keywords, jobId: id }
        if (fromProfile) await createNewInterviewKit(body)
        else {
            const newKit = { label: body.title, key: body.title }

            const updatedKits = [
                ...allKits.filter((kit: { key: string; label: string }) => !!Number(kit.key)),
                newKit,
            ];
            const kits = updatedKits.map((kit: CheckboxDropdownItem) => kit.label.trim().toLowerCase().replace('', ''));
            const uniqueKits = new Set(kits);
            const isUniqueKit = uniqueKits.size === kits.length;
            isUniqueKit ? await createNewInterviewKit(body) : message.error('Kit Title should be unique')
        }

    };

    const onFinishFailed = () => {
        setOnSubmitError(true);
    };

    const onClose = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setOnSubmitError(false);
    };

    const handleFormChange = () => {
        const hasErrors = formHasErrors(form);
        setIsDisabled(hasErrors);
        if (hasErrors) {
            setOnSubmitError(true);
        } else {
            setOnSubmitError(false);
        }
    };

    return (
        <>
            {isLoading ? (
                <div className={`w-full flex flex-col justify-center items-center ${fromProfile ? 'h-[26.25rem]' : 'h-[39rem]'}`}>
                    <LazyLoader />
                    <h3 className="mt-2 text-indigo-700">Generating kit..</h3>
                </div>
            ) : (
                <Form
                    form={form}
                        className={`text-start h-[39rem] justify-end flex items-end w-full max-w-full mx-auto generateForm ${className ?? ''}`}
                    size="large"
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                    onFieldsChange={handleFormChange}
                >
                        <div className="flex flex-col justify-between w-full h-full">
                            <div className="flex items-center justify-center w-3/4 h-full mx-auto">
                            <div className="w-full">
                                    <h1 className="py-2 pl-3 text-xs font-semibold text-gray-900 uppercase">
                                    Title and criteria
                                </h1>
                                {onSubmitError && (
                                    <Alert
                                        className="mt-4"
                                        onClose={onClose}
                                        message="Please fill the missing data"
                                        type="error"
                                        closable
                                    />
                                )}

                                <Form.Item
                                    className="mb-0 input-3xl"
                                    name="title"
                                    rules={[{ required: true, message: ValidationError }]}
                                >
                                    <Input
                                        className={`text-lg font-medium bg-transparent text-gray-600 placeholder-gray-400`}
                                        bordered={false}
                                            placeholder={`Write ${fromProfile ? 'evaluation' : 'kit'} title`}
                                    />
                                </Form.Item>

                                <Form.Item
                                    className="mb-0 md:col-span-2"
                                    name="keywords"
                                    rules={[{ required: true, message: ValidationError }]}
                                >
                                    <Input
                                        className={`text-base font-light bg-transparent text-gray-600 placeholder-gray-400`}
                                        bordered={false}
                                        placeholder="Write your interview criteria keywords separated with “,” (ex. communication, collaboration,..)"
                                    />
                                </Form.Item>
                            </div>
                        </div>
                            <div className="flex flex-col gap-4 pt-5 border-t-2 md:justify-between md:flex-row">
                                <div className="flex w-full ">
                                    {fromProfile &&
                                        <>
                                            <DropdownWithButton
                                                list={templates}
                                                imageSrc={ChevronDown}
                                                selectedTemplate={selectedTemplate}
                                                setSelectedTemplate={setSelectedTemplate}
                                                className="mr-0 rounded-r-md pr-3 btn-primary-light !border-none h-full"
                                                menuClassName="max-h-[8.5rem]"
                                                imgClassName="p-0 !mt-0 h-full"
                                                isDisabledBtn={isTemplatesLoading || isTemplatesError}
                                                isLoadingBtn={isTemplatesLoading}
                                            />
                                        </>
                                    }
                                </div>
                            <Form.Item className="mb-0">
                                <Button
                                    disabled={isDisabled}
                                    className="btn-primary !min-h-[2rem]"
                                    htmlType="submit"
                                >
                                        Generate Interview {fromProfile ? 'Questions' : 'Kit'}
                                </Button>
                            </Form.Item>
                        </div>
                    </div>
                </Form>
            )}
        </>
    );
};
